// Authentication utility functions
export const AUTH_STORAGE_KEYS = {
  TOKEN: 'token',
  AUTH_FLAG: 'unidestin_auth',
  USER: 'user',
  EXPIRES_AT: 'auth_expires_at'
};

// Extended session duration in milliseconds (2 hours for better user experience)
export const SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours

// Short session duration for quick expiration (30 seconds)
export const SHORT_SESSION_DURATION = 30 * 1000; // 30 seconds

// Set authentication data with expiration
export const setAuthData = (token, user) => {
  const expiresAt = Date.now() + SESSION_DURATION;
  
  localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
  localStorage.setItem(AUTH_STORAGE_KEYS.AUTH_FLAG, 'true');
  localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
  localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
};

// Get current user data from localStorage
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

// Get current user's role
export const getCurrentUserRole = () => {
  const user = getCurrentUser();
  return user?.role || null;
};

export const getCurrentUserPermissions = () => {
  const user = getCurrentUser();
  console.log(user?.role.permissions);
  
  return user?.role?.permissions || [];
};

// Check if user has specific permission - strictly permission-based
export const hasPermission = (permissionName) => {
  const permissions = getCurrentUserPermissions();
  return permissions.some(permission => permission.name === permissionName);
};

// Check if user has any of the specified permissions - strictly permission-based
export const hasAnyPermission = (permissionNames) => {
  return permissionNames.some(permission => hasPermission(permission));
};

// Check if user has all of the specified permissions - strictly permission-based
export const hasAllPermissions = (permissionNames) => {
  return permissionNames.every(permission => hasPermission(permission));
};

// Check if user has specific role
export const hasRole = (roleName) => {
  const role = getCurrentUserRole();
  return role?.name === roleName;
};

// Check if user has any of the specified roles
export const hasAnyRole = (roleNames) => {
  const role = getCurrentUserRole();
  return roleNames.includes(role?.name);
};

// Check if user is super admin
export const isSuperAdmin = () => {
  return hasRole('Super Admin');
};

// Permission check for modules - strictly permission-based
export const canViewModule = (module) => {
  return hasPermission(`${module}.view`);
};

export const canAddToModule = (module) => {
  return hasPermission(`${module}.add`);
};

export const canEditInModule = (module) => {
  return hasPermission(`${module}.edit`);
};

export const canDeleteInModule = (module) => {
  return hasPermission(`${module}.delete`);
};

// Legacy function for backward compatibility - now points to canAddToModule
export const canCreateInModule = (module) => {
  return canAddToModule(module);
};

// Dropdown permission checks - strictly permission-based
export const canViewUniversityDropdown = () => {
  return hasPermission('university.dropdown');
};

export const canViewStudentDropdown = () => {
  return hasPermission('student_dropdown');
};

export const canViewUserManagementDropdown = () => {
  return hasPermission('user_management.dropdown');
};

// Specific permission checks for different modules - strictly permission-based
export const canViewUniversity = () => {
  return hasPermission('university.view');
};

export const canViewCountry = () => {
  return hasPermission('country.view');
};

export const canViewCity = () => {
  return hasPermission('city.view');
};

export const canViewUniversityProgram = () => {
  return hasPermission('university_program.view');
};

export const canViewScholarship = () => {
  return hasPermission('scholarship.view');
};

export const canViewStudent = () => {
  return hasPermission('student.view');
};

export const canViewStudentRequest = () => {
  return hasPermission('student_request.view');
};

export const canViewContract = () => {
  return hasPermission('contract.view');
};

export const canViewCashManagement = () => {
  return hasPermission('cash_management.view');
};

export const canViewConsultant = () => {
  return hasPermission('consultant.view');
};

export const canViewUserManagement = () => {
  return hasPermission('user_management.view');
};

export const canViewRoleManagement = () => {
  return hasPermission('role_management.view');
};

export const canManagePermissions = () => {
  return hasPermission('permission.management');
};

export const canViewTaskManagement = () => {
  return hasPermission('task_management.view');
};

// Check if authentication is valid and not expired
export const isAuthValid = () => {
  const authFlag = localStorage.getItem(AUTH_STORAGE_KEYS.AUTH_FLAG);
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  const expiresAt = localStorage.getItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
  
  // Check if all required data exists
  if (!authFlag || authFlag !== 'true' || !token || !expiresAt) {
    return false;
  }
  
  // Check if session has expired
  const currentTime = Date.now();
  const expirationTime = parseInt(expiresAt);
  
  if (currentTime >= expirationTime) {
    // Session expired, clean up
    clearAuthData();
    return false;
  }
  
  return true;
};

// Clear all authentication data
export const clearAuthData = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH_FLAG);
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
};

// Get time remaining for session (in seconds)
export const getSessionTimeRemaining = () => {
  const expiresAt = localStorage.getItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
  
  if (!expiresAt) {
    return 0;
  }
  
  const currentTime = Date.now();
  const expirationTime = parseInt(expiresAt);
  const remaining = Math.max(0, expirationTime - currentTime);
  
  return Math.floor(remaining / 1000); // Return in seconds
};

// Extend session duration (call this on user activity)
export const extendSession = () => {
  if (isAuthValid()) {
    const newExpiresAt = Date.now() + SESSION_DURATION;
    localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, newExpiresAt.toString());
  }
};

// Set session to expire in short time (30 seconds)
export const setShortSessionExpiry = () => {
  if (isAuthValid()) {
    const newExpiresAt = Date.now() + SHORT_SESSION_DURATION;
    localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, newExpiresAt.toString());
    return true;
  }
  return false;
};

// Get token from localStorage
export const getToken = () => {
  return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
};

// Module permissions mapping - updated to match new API structure
export const MODULE_PERMISSIONS = {
  university: 'university',
  country: 'country',
  city: 'city',
  university_program: 'university_program',
  scholarship: 'scholarship',
  student: 'student',
  student_request: 'student_request',
  contract: 'contract',
  cash_management: 'cash_management',
  consultant: 'consultant',
  user_management: 'user_management',
  role_management: 'role_management',
  task_management: 'task_management'
}; 